'use client';

import { useMemo, useSyncExternalStore } from 'react';
import { Document } from '@contentful/rich-text-types';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import styles from './pznMessageHero.module.scss';
import cn from 'classnames';

function applyTokens(doc: Document, tokens: Record<string, string>): Document {
    return JSON.parse(
        JSON.stringify(doc).replace(/\{(\w+)\}/g, (match, key) => tokens[key] ?? match),
    );
}

interface PznMessageHeroProps {
    template: Document;
    fallback?: Document;
    fnFallback?: Document;
    theme?: 'shell' | 'center' | 'carousel';
}

export function PznMessageHero({ template, fallback, fnFallback, theme }: PznMessageHeroProps): React.JSX.Element {
    const rawCookie = useSyncExternalStore(
        () => () => {},
        () => document.cookie.match(/sxm_user_info=([^;]+)/)?.[1] ?? '',
        () => null,
    );

    const { resolvedDoc, visible } = useMemo(() => {
        // null = server snapshot: stay hidden to match SSR and avoid flash
        if (rawCookie === null) return { resolvedDoc: template, visible: false };

        const templateStr = JSON.stringify(template);
        const needsFn = templateStr.includes('{fn}');
        const needsJday = templateStr.includes('{jday}');

        if (rawCookie) {
            try {
                const userInfo = JSON.parse(decodeURIComponent(rawCookie));
                const fnSatisfied = !needsFn || !!userInfo.fn;
                const jdaySatisfied = !needsJday || (userInfo.jday !== undefined && userInfo.jday > 0);

                if (fnSatisfied && jdaySatisfied) {
                    const tokens: Record<string, string> = {};
                    if (needsFn) tokens.fn = userInfo.fn;
                    if (needsJday) tokens.jday = String(userInfo.jday);
                    return { resolvedDoc: applyTokens(template, tokens), visible: true };
                }

                if (needsFn && needsJday && !!userInfo.fn && !jdaySatisfied && fnFallback) {
                    return { resolvedDoc: applyTokens(fnFallback, { fn: userInfo.fn }), visible: true };
                }
            } catch {}
        }

        if (fallback) return { resolvedDoc: fallback, visible: true };
        return { resolvedDoc: template, visible: false };
    }, [template, fallback, fnFallback, rawCookie]);

    return (
        <div style={{ visibility: visible ? 'visible' : 'hidden' }} className={cn(styles.container, theme && styles[theme])}>
            <RenderRTE contentfulDoc={resolvedDoc} />
        </div>
    );
}
